import {
    ACTIVITY_FILTERS,
    ARC_FILTERS,
    DATE_FILTERS,
    DURATION_FILTERS,
    ATTRIBUTES_FILTERS,
    VARIANT_FILTERS,
    LOOP_FILTERS,
    NVARIANTS_FILTERS,
    TRACES_FILTERS,
} from '@/screens/Project/Process/Filters/FilterModal/filtersEntity'

export function ensureArray(value) {
    if (value === undefined || value === null) return []
    return Array.isArray(value) ? value : [value]
}

export function makeUuid(prefix) {
    return `${prefix}_${Math.floor(Math.random() * 10000)}`
}

export function canonicalizeFiltersToAdd(selected = {}) {
    const filtersToAdd = []

    // ACTIVITY
    if (ACTIVITY_FILTERS.includes(selected.id)) {
        const values = ensureArray(selected.values ?? selected.type?.category)
        filtersToAdd.push({
            id: selected.id,
            kind: selected.id,
            values,
            meta: { ...(selected.meta || {}) },
            pending: false,
            uuid: makeUuid(selected.id),
        })
    }

    // DATE
    if (DATE_FILTERS.includes(selected.id)) {
        const values = ensureArray(selected.values ?? selected.type?.date)
        const activity = selected.type?.category || selected.activity || (selected.meta || {}).activity
        filtersToAdd.push({
            id: selected.id,
            kind: selected.id,
            values,
            meta: {
                ...(selected.meta || {}),
                ...(activity ? { activity } : {}),
            },
            pending: false,
            uuid: makeUuid(selected.id),
        })
    }

    // DURATION
    if (DURATION_FILTERS.includes(selected.id)) {
        const duration = selected.type?.duration ?? (selected.values && selected.values[0])
        const activity = selected.type?.category || selected.activity || (selected.meta || {}).activity
        const arc = selected.type?.arc || selected.arc || (selected.meta || {}).arc

        let values = ensureArray(selected.values)
        if (!values.length && duration) values = [duration]

        // ACTIVITIES_DURATION_*: backend expects route info + duration
        if ((selected.id === 'ACTIVITIES_DURATION_GREATER_THAN' || selected.id === 'ACTIVITIES_DURATION_LESS_THAN') && selected.type) {
            values = [
                selected.type.activitySource,
                selected.type.sourceOccurrence?.value,
                selected.type.activityTarget,
                selected.type.targetOccurrence?.value,
                duration,
            ]
        }
        if ((selected.id === 'ACTIVITIES_DURATION_LOOP_GREATER_THAN' || selected.id === 'ACTIVITIES_DURATION_LOOP_LESS_THAN') && selected.type) {
            values = [
                selected.type.activitySource,
                selected.type.activityTarget,
                duration,
            ]
        }

        filtersToAdd.push({
            id: selected.id,
            kind: selected.id,
            values,
            meta: {
                ...(selected.meta || {}),
                ...(activity ? { activity } : {}),
                ...(arc ? { arc } : {}),
            },
            pending: false,
            uuid: makeUuid(selected.id),
        })
    }

    // ARC
    if (ARC_FILTERS.includes(selected.id)) {
        const values = ensureArray(selected.values ?? selected.type?.arc)
        filtersToAdd.push({
            id: selected.id,
            kind: selected.id,
            values,
            meta: { ...(selected.meta || {}) },
            pending: false,
            uuid: makeUuid(selected.id),
        })
    }

    // ATTRIBUTES (each constraint becomes its own filter id)
    if (ATTRIBUTES_FILTERS.includes(selected.type)) {
        const attributeKind = selected.type
        const attributesFilter = (selected.attributes || []).map((attr, i) => {
            const attribute = attr.attribute?.value ?? attr.attribute
            const activity = (attr.activity?.value ?? attr.activity)
            const constraintId = attr.constraints?.value
            const values = ensureArray(attr.value)

            return {
                id: constraintId,
                kind: attributeKind,
                values,
                meta: {
                    attribute,
                    ...(activity && activity !== '*' ? { activity } : {}),
                },
                pending: false,
                uuid: `${attributeKind}_${Math.floor(Math.random() * 10000)}_${i}`,
            }
        })

        filtersToAdd.push(...attributesFilter)
    }

    // VARIANTS
    if (VARIANT_FILTERS.includes(selected.id)) {
        const values = ensureArray(selected.values ?? selected.type?.variant)
        const variantName = selected.type?.variantName
        filtersToAdd.push({
            id: selected.id,
            kind: selected.id,
            values,
            meta: {
                ...(selected.meta || {}),
                ...(variantName ? { variantName } : {}),
            },
            pending: false,
            uuid: makeUuid(selected.id),
        })
    }

    // LOOPS
    if (LOOP_FILTERS.includes(selected.id)) {
        const values = ensureArray(selected.values ?? selected.type?.variant)
        const loopName = selected.type?.loopName
        filtersToAdd.push({
            id: selected.id,
            kind: selected.id,
            values,
            meta: {
                ...(selected.meta || {}),
                ...(loopName ? { loopName } : {}),
            },
            pending: false,
            uuid: makeUuid(selected.id),
        })
    }

    // TRACES
    if (TRACES_FILTERS.includes(selected.id)) {
        const values = ensureArray(selected.values)
        filtersToAdd.push({
            id: selected.id,
            kind: selected.id,
            values,
            pending: false,
            uuid: makeUuid(selected.id),
        })
    }

    // N VARIANTS
    if (NVARIANTS_FILTERS.includes(selected.id)) {
        const values = ensureArray(selected.values ?? selected.type?.nvariants)
        filtersToAdd.push({
            id: selected.id,
            kind: selected.id,
            values,
            pending: false,
            uuid: makeUuid(selected.id),
        })
    }

    return filtersToAdd
}

export function canonicalizeFilterSetAsFilter(filterSet = {}) {
    return {
        ...filterSet,
        id: 'FILTER_SET',
        kind: 'FILTER_SET',
        values: [filterSet.uuid],
        meta: { name: filterSet.name },
        pending: false,
    }
}

export function canonicalizeFiltersFromFilterSet(filters, filterSetUuid) {
    return (filters || []).map((f, index) => {
        const { id } = f
        const values = ensureArray(f.values ?? f.value)
        const loopName = f.meta?.loopName ?? f.type?.loopName
        const variantName = f.meta?.variantName ?? f.type?.variantName
        const meta = {
            ...(f.meta || {}),
            ...(f.activity ? { activity: f.activity } : {}),
            ...(f.arc ? { arc: f.arc } : {}),
            ...(f.attribute ? { attribute: f.attribute } : {}),
            ...(variantName ? { variantName: ensureArray(variantName) } : {}),
            ...(loopName
                ? { loopName: Array.isArray(loopName) ? loopName.filter(Boolean).join(', ') : loopName }
                : {}),
        }

        return {
            ...f,
            id,
            kind: f.kind || id,
            values,
            meta,
            pending: false,
            uuid: `${id}_${index}`,
            filterset_uuid: `${filterSetUuid}`,
        }
    })
}
